/**
 * "PATROL DISPLAY" — a hand-authored vector numeral face, drawn on canvas.
 *
 * WHY: ARCHITECTURE.md N1 forbids font files, and the system display stack
 * (Haettenschweiler / Arial Narrow Bold / Impact) is not guaranteed to resolve the same way
 * on every machine. The loudest type in the HUD — the countdown, the cash plate, the combo
 * number, the completion percentage — therefore does NOT use a system font at all. It is
 * stroked from explicit vector paths so it is identical everywhere and bit-reproducible.
 *
 * The face is a fat, slightly rounded condensed grotesque: the Crazy Taxi timer look.
 * Glyph coordinates live in a square 0..1 ink box (x left→right, y TOP→bottom) and are
 * mapped, without distortion of arc parameters, onto the real ink box:
 *
 *     screenX = originX + gx * inkW
 *     screenY = baselineY - capH + gy * inkH
 *
 * where inkH = capH * (1 - weight) and inkW = capH * (glyphWidth - weight). Because a
 * canvas ellipse is parametric, an arc authored at angle θ maps to exactly angle θ after the
 * non-uniform scale, so shapes stay closed at every angle.
 *
 * Path command alphabet:
 *   ['M', x, y]                          moveTo
 *   ['L', x, y]                          lineTo
 *   ['E', cx, cy, rx, ry, a0, a1, ccw]   elliptical arc, angles in DEGREES
 *   ['O', cx, cy, rx, ry]                closed full ellipse (its own subpath)
 *   ['Z']                                closePath
 *
 * Letters that are not in the set fall back to `drawLabel` (system display stack), which is
 * used for all label/word type where an exact glyph shape does not matter.
 */

const D = Math.PI / 180;

/** Per-glyph advance width, in cap-height units, BEFORE the stroke weight is added. */
const WIDTH = {
  default: 0.46,
  '1': 0.40, '.': 0.20, ',': 0.22, ':': 0.20, "'": 0.20,
  '-': 0.42, '+': 0.50, '/': 0.42, '%': 0.72, '×': 0.50, ' ': 0.30,
  '$': 0.52, '°': 0.34,
};

/**
 * The glyph table. Digits first — they carry the whole HUD.
 */
const G = {
  '0': [['M', 0, 0.30], ['E', 0.5, 0.30, 0.5, 0.30, 180, 360, false], ['L', 1, 0.70],
        ['E', 0.5, 0.70, 0.5, 0.30, 0, 180, false], ['Z']],

  '1': [['M', 0.06, 0.26], ['L', 0.52, 0.0], ['L', 0.52, 1.0]],

  '2': [['M', 0.02, 0.32], ['E', 0.5, 0.32, 0.48, 0.30, 180, 360, false], ['L', 0.98, 0.40],
        ['L', 0.06, 1.0], ['L', 1.0, 1.0]],

  '3': [['M', 0.06, 0.03], ['L', 0.48, 0.03], ['E', 0.5, 0.29, 0.5, 0.26, 270, 90, false],
        ['E', 0.5, 0.74, 0.5, 0.26, 270, 90, false], ['L', 0.06, 1.0]],

  '4': [['M', 0.72, 0.0], ['L', 0.02, 0.70], ['L', 1.0, 0.70], ['M', 0.72, 0.0], ['L', 0.72, 1.0]],

  '5': [['M', 0.96, 0.03], ['L', 0.14, 0.03], ['L', 0.12, 0.42], ['L', 0.48, 0.40],
        ['E', 0.5, 0.70, 0.5, 0.30, 270, 90, false], ['L', 0.06, 1.0]],

  '6': [['O', 0.5, 0.66, 0.5, 0.34], ['M', 0.88, 0.02],
        ['E', 0.52, 0.36, 0.52, 0.34, 270, 180, true], ['L', 0.0, 0.66]],

  '7': [['M', 0.02, 0.03], ['L', 0.98, 0.03], ['L', 0.28, 1.0]],

  '8': [['O', 0.5, 0.25, 0.46, 0.25], ['O', 0.5, 0.74, 0.5, 0.26]],

  '9': [['O', 0.5, 0.34, 0.5, 0.34], ['M', 0.12, 0.98],
        ['E', 0.48, 0.64, 0.52, 0.34, 90, 0, true], ['L', 1.0, 0.34]],

  '.': [['M', 0.5, 0.995], ['L', 0.5, 1.0]],
  ',': [['M', 0.62, 0.94], ['L', 0.34, 1.22]],
  ':': [['M', 0.5, 0.30], ['L', 0.5, 0.305], ['M', 0.5, 0.90], ['L', 0.5, 0.905]],
  "'": [['M', 0.5, 0.02], ['L', 0.40, 0.28]],
  '-': [['M', 0.06, 0.56], ['L', 0.94, 0.56]],
  '+': [['M', 0.06, 0.58], ['L', 0.94, 0.58], ['M', 0.5, 0.24], ['L', 0.5, 0.92]],
  '/': [['M', 0.06, 1.0], ['L', 0.94, 0.0]],
  '×': [['M', 0.10, 0.30], ['L', 0.90, 0.92], ['M', 0.90, 0.30], ['L', 0.10, 0.92]],
  '°': [['O', 0.5, 0.18, 0.42, 0.16]],
  '%': [['O', 0.24, 0.20, 0.22, 0.18], ['O', 0.76, 0.80, 0.22, 0.18],
        ['M', 0.96, 0.02], ['L', 0.04, 0.98]],

  // A short alphabet: the rank letters and the few words drawn in the vector face.
  'S': [['M', 0.98, 0.20], ['E', 0.5, 0.29, 0.5, 0.27, -30, -180, true],
        ['E', 0.5, 0.72, 0.5, 0.27, -70, 150, false]],
  '$': [['M', 0.98, 0.22], ['E', 0.5, 0.31, 0.5, 0.25, -30, -180, true],
        ['E', 0.5, 0.70, 0.5, 0.25, -70, 150, false], ['M', 0.5, -0.10], ['L', 0.5, 1.10]],
  'A': [['M', 0.02, 1.0], ['L', 0.5, 0.0], ['L', 0.98, 1.0], ['M', 0.18, 0.70], ['L', 0.82, 0.70]],
  'B': [['M', 0.06, 0.0], ['L', 0.06, 1.0], ['M', 0.06, 0.0], ['L', 0.44, 0.0],
        ['E', 0.44, 0.26, 0.5, 0.26, 270, 90, false], ['L', 0.06, 0.52],
        ['M', 0.06, 0.52], ['L', 0.44, 0.52], ['E', 0.44, 0.76, 0.52, 0.24, 270, 90, false],
        ['L', 0.06, 1.0]],
  'C': [['M', 0.98, 0.16], ['E', 0.5, 0.30, 0.5, 0.28, -40, -180, true], ['L', 0.0, 0.70],
        ['E', 0.5, 0.70, 0.5, 0.28, 180, 320, false]],
  'D': [['M', 0.06, 0.0], ['L', 0.06, 1.0], ['M', 0.06, 0.0], ['L', 0.42, 0.0],
        ['E', 0.42, 0.5, 0.56, 0.5, 270, 90, false], ['L', 0.06, 1.0]],
  'R': [['M', 0.06, 1.0], ['L', 0.06, 0.0], ['L', 0.44, 0.0],
        ['E', 0.44, 0.26, 0.5, 0.26, 270, 90, false], ['L', 0.06, 0.52],
        ['M', 0.40, 0.52], ['L', 0.98, 1.0]],
  ' ': [],
};

/** Glyph advance in cap-height units, including the stroke. */
export function advanceOf(ch, weight, tracking) {
  const w = WIDTH[ch] != null ? WIDTH[ch] : WIDTH.default;
  return w + weight + tracking;
}

export function measure(text, capH, weight, tracking) {
  let w = 0;
  for (let i = 0; i < text.length; i++) w += advanceOf(text[i], weight, tracking);
  return w * capH - tracking * capH;
}

/** True when every character in `text` exists in the vector face. */
export function covers(text) {
  for (let i = 0; i < text.length; i++) if (!G[text[i]]) return false;
  return true;
}

function buildGlyphPath(g, cmds, ox, oy, inkW, inkH) {
  for (let i = 0; i < cmds.length; i++) {
    const c = cmds[i];
    switch (c[0]) {
      case 'M': g.moveTo(ox + c[1] * inkW, oy + c[2] * inkH); break;
      case 'L': g.lineTo(ox + c[1] * inkW, oy + c[2] * inkH); break;
      case 'E': g.ellipse(ox + c[1] * inkW, oy + c[2] * inkH, c[3] * inkW, c[4] * inkH, 0,
        c[5] * D, c[6] * D, !!c[7]); break;
      case 'O':
        g.moveTo(ox + (c[1] + c[3]) * inkW, oy + c[2] * inkH);
        g.ellipse(ox + c[1] * inkW, oy + c[2] * inkH, c[3] * inkW, c[4] * inkH, 0, 0, Math.PI * 2, false);
        break;
      case 'Z': g.closePath(); break;
      default: break;
    }
  }
}

const OPT = {
  capH: 100, weight: 0.24, tracking: 0.04, skewDeg: 0,
  fill: '#FFF200', fillLow: null, outline: 0.05, outlineColor: '#0C120D',
  shadow: 0, shadowAlpha: 0.4, shadowDx: 0, shadowDy: 0,
  align: 'left', scale: 1,
};

/**
 * Draw a string in the vector face.
 *
 * @param g   CanvasRenderingContext2D
 * @param text
 * @param x   pen x (meaning depends on `align`)
 * @param y   BASELINE y
 * @param o   options; see OPT for defaults
 */
export function drawNum(g, text, x, y, o) {
  const capH = o.capH != null ? o.capH : OPT.capH;
  const weight = o.weight != null ? o.weight : OPT.weight;
  const tracking = o.tracking != null ? o.tracking : OPT.tracking;
  const skew = (o.skewDeg != null ? o.skewDeg : OPT.skewDeg) * D;
  const outline = o.outline != null ? o.outline : OPT.outline;
  const scale = o.scale != null ? o.scale : 1;

  const total = measure(text, capH, weight, tracking);
  let penX = x;
  if (o.align === 'right') penX = x - total * scale;
  else if (o.align === 'center') penX = x - total * scale * 0.5;

  const inkH = capH * (1 - weight);
  const sw = capH * weight;

  g.save();
  // Scale about the baseline-left of the run so pops grow from the type, not the canvas.
  if (scale !== 1) {
    g.translate(penX, y);
    g.scale(scale, scale);
    g.translate(-penX, -y);
  }
  // Italic skew about the baseline.
  if (skew !== 0) {
    g.translate(0, y);
    g.transform(1, 0, Math.tan(skew), 1, 0, 0);
    g.translate(0, -y);
  }

  const path = new Path2D();
  let cursor = penX;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const cmds = G[ch];
    const gw = (WIDTH[ch] != null ? WIDTH[ch] : WIDTH.default);
    if (cmds && cmds.length) {
      const inkW = capH * gw;
      buildGlyphPath(path, cmds, cursor + sw * 0.5, y - capH + sw * 0.5, inkW, inkH);
    }
    cursor += (gw + weight + tracking) * capH;
  }

  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.miterLimit = 2;

  // 1. drop shadow
  if (o.shadowDx || o.shadowDy) {
    g.save();
    g.translate(o.shadowDx, o.shadowDy);
    g.strokeStyle = `rgba(0,0,0,${o.shadowAlpha != null ? o.shadowAlpha : OPT.shadowAlpha})`;
    g.lineWidth = sw + capH * outline * 2;
    g.stroke(path);
    g.restore();
  }

  // 2. outline
  if (outline > 0) {
    g.strokeStyle = o.outlineColor || OPT.outlineColor;
    g.lineWidth = sw + capH * outline * 2;
    g.stroke(path);
  }

  // 3. face — a vertical gradient reads as a bevel without a second pass
  if (o.fillLow) {
    const grad = g.createLinearGradient(0, y - capH, 0, y);
    grad.addColorStop(0, o.fill || OPT.fill);
    grad.addColorStop(0.58, o.fill || OPT.fill);
    grad.addColorStop(1, o.fillLow);
    g.strokeStyle = grad;
  } else {
    g.strokeStyle = o.fill || OPT.fill;
  }
  g.lineWidth = sw;
  g.stroke(path);

  // 4. specular sliver along the top of the stroke
  if (o.gloss) {
    g.save();
    g.translate(0, -sw * 0.26);
    g.strokeStyle = o.gloss;
    g.lineWidth = sw * 0.24;
    g.globalAlpha = 0.85;
    g.stroke(path);
    g.restore();
  }

  g.restore();
  return total * scale;
}

// --------------------------------------------------------------------------- label type

/**
 * Word/label type using the system display stack. Used only where the exact glyph shape is
 * not load-bearing (headers, names, callout words). Manual per-character advance so tracking
 * is identical regardless of the browser's letter-spacing support.
 */
export function drawLabel(g, text, x, y, o) {
  const size = o.size || 32;
  const family = o.family;
  const weight = o.weight || 700;
  const tracking = o.tracking || 0;
  const squeeze = o.squeeze != null ? o.squeeze : 0.94;
  const skew = (o.skewDeg || 0) * D;
  const scale = o.scale != null ? o.scale : 1;

  g.save();
  g.font = `${weight} ${size}px ${family}`;
  g.textBaseline = 'alphabetic';
  g.textAlign = 'left';

  let total = 0;
  for (let i = 0; i < text.length; i++) total += g.measureText(text[i]).width * squeeze + tracking;
  total -= tracking;

  let penX = x;
  if (o.align === 'right') penX = x - total * scale;
  else if (o.align === 'center') penX = x - total * scale * 0.5;

  if (scale !== 1) {
    g.translate(penX, y);
    g.scale(scale, scale);
    g.translate(-penX, -y);
  }
  if (skew !== 0) {
    g.translate(0, y);
    g.transform(1, 0, Math.tan(skew), 1, 0, 0);
    g.translate(0, -y);
  }

  g.lineJoin = 'round';
  g.miterLimit = 2;

  const drawPass = (mode, style, lw, dx, dy) => {
    let cx = penX;
    g.save();
    if (dx || dy) g.translate(dx, dy);
    if (mode === 'stroke') { g.strokeStyle = style; g.lineWidth = lw; } else { g.fillStyle = style; }
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const w = g.measureText(ch).width;
      g.save();
      g.translate(cx, y);
      g.scale(squeeze, 1);
      if (mode === 'stroke') g.strokeText(ch, 0, 0); else g.fillText(ch, 0, 0);
      g.restore();
      cx += w * squeeze + tracking;
    }
    g.restore();
  };

  if (o.shadowDx || o.shadowDy) {
    drawPass('fill', `rgba(0,0,0,${o.shadowAlpha != null ? o.shadowAlpha : 0.4})`, 0, o.shadowDx || 0, o.shadowDy || 0);
  }
  if (o.outline) drawPass('stroke', o.outlineColor || '#0C120D', o.outline * 2, 0, 0);
  drawPass('fill', o.fill || '#FFFFFF', 0, 0, 0);

  g.restore();
  return total * scale;
}

export function measureLabel(g, text, o) {
  const size = o.size || 32;
  const tracking = o.tracking || 0;
  const squeeze = o.squeeze != null ? o.squeeze : 0.94;
  g.save();
  g.font = `${o.weight || 700} ${size}px ${o.family}`;
  let total = 0;
  for (let i = 0; i < text.length; i++) total += g.measureText(text[i]).width * squeeze + tracking;
  g.restore();
  return total - tracking;
}
